/**
 * Security Service
 * Implements guardrails and security measures
 * Fromas Cloud AI Assistant
 */

export class SecurityService {
  private readonly PROMPT_INJECTION_PATTERNS = [
    /ignore\s+(previous|above|all)\s+instructions?/i,
    /disregard\s+(previous|above|all)\s+instructions?/i,
    /forget\s+(previous|above|all)\s+instructions?/i,
    /you\s+are\s+now/i,
    /new\s+instructions?:/i,
    /system\s*:/i,
    /assistant\s*:/i,
    /<\s*script/i,
    /javascript:/i,
    /onerror\s*=/i,
    /onclick\s*=/i
  ];

  private readonly OFFENSIVE_PATTERNS: RegExp[] = [
    // Add patterns for offensive content detection
    // This is a simplified version
    // Example patterns (customize based on your needs):
    // /badword1/i,
    // /badword2/i,
  ];

  private readonly MAX_INPUT_LENGTH = 1000;
  private readonly MAX_REQUESTS_PER_MINUTE = 20;
  private requestHistory: Map<string, number[]> = new Map();

  /**
   * Sanitize user input
   */
  sanitizeInput(input: string): string {
    if (!input) return '';

    let sanitized = input;

    // Remove script tags and dangerous HTML
    sanitized = sanitized.replace(/<script[^>]*>.*?<\/script>/gi, '');
    sanitized = sanitized.replace(/<iframe[^>]*>.*?<\/iframe>/gi, '');
    sanitized = sanitized.replace(/javascript:/gi, '');
    sanitized = sanitized.replace(/on\w+\s*=/gi, '');

    // Normalize whitespace
    sanitized = sanitized.replace(/\s+/g, ' ').trim();

    // Limit length
    if (sanitized.length > this.MAX_INPUT_LENGTH) {
      sanitized = sanitized.substring(0, this.MAX_INPUT_LENGTH);
    }

    return sanitized;
  }

  /**
   * Check for prompt injection attempts
   */
  checkPromptInjection(input: string): boolean {
    if (!input) return true;

    const lowerInput = input.toLowerCase();

    // Check against known prompt injection patterns
    for (const pattern of this.PROMPT_INJECTION_PATTERNS) {
      if (pattern.test(input)) {
        console.warn('Potential prompt injection detected:', input.substring(0, 100));
        return false;
      }
    }

    // Check for excessive special characters (possible obfuscation)
    const specialCharRatio = (input.match(/[^a-zA-Z0-9\s\u0E00-\u0E7F]/g) || []).length / input.length;
    if (specialCharRatio > 0.3) {
      console.warn('Excessive special characters detected');
      return false;
    }

    // Check for very long tokens (possible token smuggling)
    const tokens = input.split(/\s+/);
    const hasLongToken = tokens.some(token => token.length > 50);
    if (hasLongToken) {
      console.warn('Unusually long token detected');
      return false;
    }

    return true;
  }

  /**
   * Check for offensive content
   */
  checkOffensiveContent(input: string): boolean {
    if (!input) return true;

    const lowerInput = input.toLowerCase();

    for (const pattern of this.OFFENSIVE_PATTERNS) {
      if (pattern.test(lowerInput)) {
        console.warn('Offensive content detected');
        return false;
      }
    }

    return true;
  }

  /**
   * Rate limiting check
   */
  checkRateLimit(userId: string = 'anonymous'): boolean {
    const now = Date.now();
    const userHistory = this.requestHistory.get(userId) || [];

    // Remove requests older than 1 minute
    const recentRequests = userHistory.filter(timestamp => now - timestamp < 60000);

    // Check if user exceeded rate limit
    if (recentRequests.length >= this.MAX_REQUESTS_PER_MINUTE) {
      console.warn(`Rate limit exceeded for user: ${userId}`);
      return false;
    }

    // Add current request
    recentRequests.push(now);
    this.requestHistory.set(userId, recentRequests);

    // Clean up old entries periodically
    if (this.requestHistory.size > 1000) {
      this.cleanupRateLimitHistory();
    }

    return true;
  }

  /**
   * Mask sensitive data in text
   */
  maskSensitiveData(text: string): string {
    let masked = text;

    // Mask email addresses
    masked = masked.replace(
      /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/gi,
      (match) => {
        const [user, domain] = match.split('@');
        return `${user.substring(0, 2)}***@${domain}`;
      }
    );

    // Mask phone numbers (Thai format)
    masked = masked.replace(
      /0[0-9]{1,2}[-\s]?[0-9]{3,4}[-\s]?[0-9]{4}/g,
      (match) => {
        return `0**-***-${match.slice(-4)}`;
      }
    );

    // Mask credit card numbers
    masked = masked.replace(
      /\b[0-9]{4}[\s-]?[0-9]{4}[\s-]?[0-9]{4}[\s-]?[0-9]{4}\b/g,
      (match) => {
        return `****-****-****-${match.slice(-4)}`;
      }
    );

    // Mask Thai ID numbers
    masked = masked.replace(
      /\b[0-9]{1}[-\s]?[0-9]{4}[-\s]?[0-9]{5}[-\s]?[0-9]{2}[-\s]?[0-9]{1}\b/g,
      '***********'
    );

    return masked;
  }

  /**
   * Validate response before returning to user
   */
  validateResponse(response: string): { isValid: boolean; reason?: string } {
    if (!response || response.trim().length === 0) {
      return { isValid: false, reason: 'Empty response' };
    }

    // Check for leaked system prompts
    if (response.toLowerCase().includes('you are an ai') ||
        response.toLowerCase().includes('as an ai assistant') ||
        response.toLowerCase().includes('my instructions are')) {
      return { isValid: false, reason: 'Potential system prompt leak' };
    }

    // Check for code injection in response
    if (response.includes('<script') || response.includes('javascript:')) {
      return { isValid: false, reason: 'Potential code injection' };
    }

    // Check response length
    if (response.length > 5000) {
      return { isValid: false, reason: 'Response too long' };
    }

    return { isValid: true };
  }

  /**
   * Generate security report
   */
  generateSecurityReport(): {
    totalRequests: number;
    blockedRequests: number;
    activeUsers: number;
  } {
    let totalRequests = 0;
    let activeUsers = 0;

    this.requestHistory.forEach((requests, userId) => {
      totalRequests += requests.length;
      if (requests.length > 0) {
        activeUsers++;
      }
    });

    return {
      totalRequests,
      blockedRequests: 0, // Would track this in production
      activeUsers
    };
  }

  /**
   * Clean up old rate limit history
   */
  private cleanupRateLimitHistory(): void {
    const now = Date.now();
    const cutoff = now - 3600000; // 1 hour ago

    this.requestHistory.forEach((timestamps, userId) => {
      const recentTimestamps = timestamps.filter(ts => ts > cutoff);
      
      if (recentTimestamps.length === 0) {
        this.requestHistory.delete(userId);
      } else {
        this.requestHistory.set(userId, recentTimestamps);
      }
    });
  }

  /**
   * Log security event
   */
  logSecurityEvent(event: {
    type: 'prompt_injection' | 'rate_limit' | 'offensive_content' | 'validation_error';
    severity: 'low' | 'medium' | 'high';
    details: string;
    userId?: string;
  }): void {
    const logEntry = {
      timestamp: new Date().toISOString(),
      ...event
    };


    // In production, send to security monitoring service
    try {
      const logs = JSON.parse(localStorage.getItem('fromas_security_logs') || '[]');
      logs.push(logEntry);
      
      // Keep only last 100 logs
      if (logs.length > 100) {
        logs.shift();
      }
      
      localStorage.setItem('fromas_security_logs', JSON.stringify(logs));
    } catch (error) {
      console.error('Failed to log security event:', error);
    }
  }
}

